/**
 * UTM Tracker Utility
 *
 * Reads UTM parameters (and gclid) from the current URL, persists them in
 * sessionStorage AND in cookies (so PHP/GF can read them server-side), and
 * exposes helpers to:
 *  - retrieve the collected params
 *  - populate Gravity Forms hidden fields automatically
 */

const UTM_KEYS = [
    'utm_source',
    'utm_campaign',
    'utm_medium',
    'utm_term',
    'utm_keyword',
    'utm_content',
    'gclid',
]

const STORAGE_KEY = 'utm_params'
// Cookie duration: 30 days
const COOKIE_MAX_AGE = 30 * 24 * 60 * 60

/**
 * Persist UTM params as individual cookies so PHP (and Gravity Forms
 * gform_field_value_{key} hooks) can read them server-side.
 * Cookie name pattern: {key}  e.g. utm_source
 * @param {Object} params
 */
function saveToCookies(params) {
    Object.entries(params).forEach(([key, value]) => {
        document.cookie = [
            `${key}=${encodeURIComponent(value)}`,
            'path=/',
            `max-age=${COOKIE_MAX_AGE}`,
            'SameSite=Lax',
        ].join('; ')
    })
}

/**
 * Extract UTM keys present in the current page URL.
 * @returns {Object}
 */
function readFromUrl() {
    const params = new URLSearchParams(window.location.search)
    const result = {}

    UTM_KEYS.forEach(key => {
        const value = params.get(key)
        if (value) {
            result[key] = value
        }
    })

    return result
}

/**
 * Load previously stored UTM params from sessionStorage.
 * @returns {Object}
 */
function readFromStorage() {
    try {
        return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {}
    } catch (_) {
        return {}
    }
}

/**
 * Persist UTM params to sessionStorage.
 * @param {Object} params
 */
function saveToStorage(params) {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(params))
    } catch (_) {
        // Silently fail if sessionStorage is unavailable.
    }
}

/**
 * Return the current set of UTM params.
 * URL values take precedence and are merged with anything previously stored
 * so parameters are carried across pages even when they disappear from the URL.
 * @returns {Object}
 */
export function getUtmParams() {
    const fromUrl     = readFromUrl()
    const fromStorage = readFromStorage()

    // URL params override stored values; previously stored keys are kept if
    // they are no longer present in the URL.
    const merged = { ...fromStorage, ...fromUrl }

    if (Object.keys(fromUrl).length > 0) {
        saveToStorage(merged)
        saveToCookies(merged)
    }

    return merged
}

/**
 * Populate Gravity Forms hidden fields with UTM params.
 *
 * Two strategies are applied for maximum compatibility:
 *
 * 1. `gform_field_values` — GF's built-in mechanism for pre-populating fields
 *    whose "Allow field to be populated dynamically" is enabled in the admin
 *    with a matching Parameter Name.
 *
 * 2. Direct hidden input injection — each UTM key is injected as a hidden
 *    `<input>` into every GF form so that custom server-side handlers (or
 *    future GF field mappings) can read them from $_POST.
 */
export function populateGravityForms() {
    const utms = getUtmParams()

    if (Object.keys(utms).length === 0) {
        return
    }

    // Build the gform_field_values query string:  key=value&key2=value2
    const fieldValues = Object.entries(utms)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join('&')

    /*document.querySelectorAll('form[id^="gform_"]').forEach(form => {

        // --- Strategy 1: gform_field_values ---
        const gfFieldValues = form.querySelector('input[name="gform_field_values"]')
        if (gfFieldValues) {
            // Merge with any pre-existing value set by GF.
            const existing = gfFieldValues.value ? gfFieldValues.value + '&' : ''
            gfFieldValues.value = existing + fieldValues
        }

        // --- Strategy 2: direct hidden inputs ---
        UTM_KEYS.forEach(key => {
            const value = utms[key] || ''

            // Re-use an existing field with a matching name if one exists.
            let input = form.querySelector(`input[name="${key}"]`)

            if (!input) {
                input = document.createElement('input')
                input.type  = 'hidden'
                input.name  = key
                form.appendChild(input)
            }

            input.value = value
        })
    })*/
}
